import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBusinessProfiles } from '../../hooks/useBusinessProfiles';
import { useGenerationCount, FREE_LIMIT } from '../../hooks/useGenerationCount';
import { useUserPlan } from '../../hooks/useUserPlan';
import { useAdHistory } from '../../hooks/useAdHistory';

const platformColors: Record<string, string> = {
  Facebook: 'bg-blue-50 text-blue-600',
  Instagram: 'bg-pink-50 text-pink-600',
  Google: 'bg-amber-50 text-amber-600',
  TikTok: 'bg-stone-100 text-stone-700',
  LinkedIn: 'bg-sky-50 text-sky-700',
};

function StatCard({ icon, label, value, sub, accent }: { icon: string; label: string; value: string | number; sub?: string; accent?: string }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-100 p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider">{label}</span>
        <div className={`w-8 h-8 flex items-center justify-center rounded-xl ${accent ?? 'bg-stone-100 text-stone-500'}`}>
          <i className={`${icon} text-sm`} />
        </div>
      </div>
      <div className="text-2xl font-bold text-stone-900 mb-0.5">{value}</div>
      {sub && <div className="text-xs text-stone-400">{sub}</div>}
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { profiles, loading: profilesLoading } = useBusinessProfiles();
  const { usedCount, remaining, loading: countLoading } = useGenerationCount();
  const { plan, profileLimit, generationLimit } = useUserPlan();
  const { history, loading: historyLoading } = useAdHistory(5);

  const displayName = (user?.user_metadata?.full_name as string | undefined) ?? user?.email?.split('@')[0] ?? 'User';
  const firstName = displayName.split(' ')[0];

  const planLabel = plan === 'free' ? 'Free' : plan === 'pro' ? 'Pro' : 'Agency';
  const generationsLabel = generationLimit
    ? `${usedCount} / ${generationLimit} used`
    : `${usedCount} total`;

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Top bar */}
      <header className="bg-white border-b border-stone-100 flex items-center justify-between px-4 md:px-6 h-14 md:h-16 shrink-0 sticky top-0 z-10 gap-2">
        <div className="min-w-0 shrink">
          <h1 className="text-sm md:text-base font-bold text-stone-900">Dashboard</h1>
          <p className="text-xs text-stone-400 hidden sm:block">Your AI marketing workspace</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/dashboard/profiles/new"
            className="flex items-center gap-1.5 text-xs font-semibold text-stone-600 bg-stone-100 hover:bg-stone-200 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            <span className="hidden sm:inline">New Profile</span>
          </Link>
          <Link
            to="/dashboard/social-post"
            className="flex items-center gap-1.5 text-xs font-semibold text-stone-600 bg-stone-100 hover:bg-stone-200 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-sparkling-2-fill text-orange-400" />
            <span className="hidden sm:inline">Social Post</span>
          </Link>
          <Link
            to="/dashboard/generate"
            className="flex items-center gap-1.5 text-xs font-bold bg-gradient-to-r from-orange-500 to-amber-400 text-white px-3 py-2 rounded-lg hover:from-orange-600 hover:to-amber-500 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-flashlight-fill" />
            <span className="hidden sm:inline">Generate Ads</span>
            <span className="sm:hidden">Generate</span>
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 md:py-6 space-y-7">
        {/* Welcome */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-stone-900">Welcome back, {firstName} 👋</h2>
            <p className="text-sm text-stone-500 mt-0.5">Here's what's happening in your workspace today.</p>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            icon="ri-vip-crown-line"
            label="Current Plan"
            value={planLabel}
            sub="Upgrade for more features"
            accent="bg-orange-100 text-orange-500"
          />
          {!countLoading && (
            <StatCard
              icon="ri-flashlight-fill"
              label="Ad Generations"
              value={usedCount}
              sub={generationLimit ? `${remaining} remaining this period` : 'Unlimited plan active'}
              accent="bg-amber-100 text-amber-600"
            />
          )}
          {!profilesLoading && (
            <StatCard
              icon="ri-building-2-line"
              label="Business Profiles"
              value={profiles.length}
              sub={profileLimit < 999 ? `${profileLimit - profiles.length} slots remaining` : 'Unlimited profiles'}
              accent="bg-stone-100 text-stone-500"
            />
          )}
        </div>

        {/* Quota warning */}
        {!countLoading && generationLimit && remaining <= 1 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100 shrink-0">
                <i className="ri-error-warning-line text-amber-500" />
              </div>
              <div>
                <p className="text-sm font-bold text-stone-800">
                  {remaining === 0 ? "You've used all free generations." : `Only ${remaining} generation left on your free plan.`}
                </p>
                <p className="text-xs text-stone-500 mt-0.5">Upgrade to Pro for unlimited ad generation.</p>
              </div>
            </div>
            <Link
              to="/pricing"
              className="shrink-0 flex items-center gap-1.5 text-xs font-bold bg-gradient-to-r from-orange-500 to-amber-400 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-amber-500 transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-flashlight-fill" />
              Upgrade Now
            </Link>
          </div>
        )}

        {/* Quick actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/dashboard/generate"
            className="group bg-gradient-to-br from-orange-500 to-amber-400 rounded-2xl p-6 flex items-center gap-4 cursor-pointer hover:from-orange-600 hover:to-amber-500 transition-all duration-200"
          >
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/20 shrink-0">
              <i className="ri-flashlight-fill text-white text-2xl" />
            </div>
            <div>
              <div className="text-base font-bold text-white">Generate Ads</div>
              <div className="text-sm text-white/80 mt-0.5">High-converting ad copy &amp; creatives</div>
            </div>
            <i className="ri-arrow-right-line text-white/60 ml-auto text-xl group-hover:translate-x-1 transition-transform" />
          </Link>

          <Link
            to="/dashboard/social-post"
            className="group bg-white border border-stone-200 rounded-2xl p-6 flex items-center gap-4 cursor-pointer hover:border-orange-300 transition-all duration-200"
          >
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-orange-50 shrink-0">
              <i className="ri-sparkling-2-fill text-orange-500 text-2xl" />
            </div>
            <div>
              <div className="text-base font-bold text-stone-900">Social Posts</div>
              <div className="text-sm text-stone-500 mt-0.5">Organic content for any platform</div>
            </div>
            <i className="ri-arrow-right-line text-stone-300 ml-auto text-xl group-hover:text-orange-400 transition-colors" />
          </Link>

          <Link
            to="/dashboard/profiles/new"
            className="group bg-white border border-stone-200 rounded-2xl p-6 flex items-center gap-4 cursor-pointer hover:border-orange-300 transition-all duration-200"
          >
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-stone-100 shrink-0">
              <i className="ri-building-2-line text-stone-600 text-2xl" />
            </div>
            <div>
              <div className="text-base font-bold text-stone-900">New Profile</div>
              <div className="text-sm text-stone-500 mt-0.5">Save business info for reuse</div>
            </div>
            <i className="ri-arrow-right-line text-stone-300 ml-auto text-xl group-hover:text-orange-400 transition-colors" />
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Business Profiles */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-stone-800">Business Profiles</h3>
              <Link to="/dashboard/profiles" className="text-xs text-orange-500 hover:text-orange-600 font-semibold">
                View all →
              </Link>
            </div>

            {profilesLoading ? (
              <div className="space-y-3">
                {[1, 2].map(i => (
                  <div key={i} className="bg-white rounded-xl border border-stone-100 h-20 animate-pulse" />
                ))}
              </div>
            ) : profiles.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-stone-200 p-6 text-center">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-stone-100 mx-auto mb-3">
                  <i className="ri-building-2-line text-stone-400 text-lg" />
                </div>
                <p className="text-sm font-semibold text-stone-700 mb-1">No profiles yet</p>
                <p className="text-xs text-stone-400 mb-4 leading-relaxed">Create your first Business Profile to start generating personalized ads.</p>
                <Link
                  to="/dashboard/profiles/new"
                  className="inline-flex items-center gap-1.5 text-xs font-bold bg-gradient-to-r from-orange-500 to-amber-400 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-amber-500 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line" />
                  Create Profile
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {profiles.slice(0, 3).map(p => (
                  <div key={p.id} className="bg-white rounded-xl border border-stone-100 p-4 hover:border-orange-200 transition-all duration-200">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-orange-100 to-amber-50 shrink-0">
                          <i className="ri-building-2-line text-orange-400 text-sm" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-stone-900 truncate">{p.business_name}</div>
                          <div className="text-xs text-stone-400 truncate">{p.business_type}</div>
                        </div>
                      </div>
                      <Link
                        to="/dashboard/generate"
                        state={{ profileId: p.id }}
                        className="shrink-0 flex items-center gap-1 text-xs font-semibold text-orange-500 hover:text-orange-600 bg-orange-50 hover:bg-orange-100 px-2.5 py-1.5 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-flashlight-fill text-xs" />
                        Generate
                      </Link>
                    </div>
                    {p.location && (
                      <div className="flex items-center gap-1.5 mt-2 text-xs text-stone-400">
                        <i className="ri-map-pin-line text-xs" />
                        {p.location}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent History */}
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-stone-800">Recent Generations</h3>
              <Link to="/dashboard/history" className="text-xs text-orange-500 hover:text-orange-600 font-semibold">
                View all →
              </Link>
            </div>

            {historyLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-white rounded-xl border border-stone-100 h-16 animate-pulse" />
                ))}
              </div>
            ) : history.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-stone-200 p-8 text-center">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-stone-100 mx-auto mb-3">
                  <i className="ri-history-line text-stone-400 text-lg" />
                </div>
                <p className="text-sm font-semibold text-stone-700 mb-1">No generations yet</p>
                <p className="text-xs text-stone-400 leading-relaxed">Your generated ads will appear here so you can review them anytime.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {history.map(item => (
                  <div key={item.id} className="bg-white rounded-xl border border-stone-100 px-4 py-3.5 hover:border-stone-200 transition-all duration-200">
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-md shrink-0 ${platformColors[item.platform ?? ''] ?? 'bg-stone-100 text-stone-600'}`}>
                        {item.platform ?? 'Ad'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-stone-800 truncate">
                          {item.business_name ?? 'Unknown Business'}
                        </div>
                        {item.headlines && item.headlines.length > 0 && (
                          <div className="text-xs text-stone-400 truncate mt-0.5">
                            {item.headlines[0]}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-stone-400 shrink-0">{formatDate(item.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
